import * as React from 'react';
import { cn } from '@/core/lib/utils';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { floatingInputVariants, floatingLabelVariants } from './FloatingLabelInput';
import type { VariantProps } from 'class-variance-authority';

interface FloatingLabelSelectProps extends VariantProps<typeof floatingInputVariants> {
  label: string;
  id: string;
  value?: string;
  onValueChange: (value: string) => void;
  hasValue?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function FloatingLabelSelect({
  label,
  id,
  value,
  onValueChange,
  hasValue = false,
  invalid = false,
  disabled = false,
  variant,
  children,
  className,
  'data-testid': testId,
}: FloatingLabelSelectProps) {
  const [isOpen, setIsOpen] = React.useState(false);

  const isFloating = isOpen || hasValue;

  return (
    <div className="relative w-full flex items-center">
      {/* Border wrapper — provides visual outline matching floatingInputVariants */}
      <div
        className={cn(
          floatingInputVariants({ variant }),
          'p-0 flex items-center w-full',
          invalid && 'outline-destructive ring-2 ring-destructive/20',
          className,
        )}
      >
        <Select
          value={value}
          onValueChange={onValueChange}
          onOpenChange={setIsOpen}
          disabled={disabled}
        >
          <SelectTrigger
            id={id}
            aria-invalid={invalid}
            className="border-0 shadow-none outline-none ring-0 focus-visible:ring-0 px-3 w-full bg-transparent rounded-lg text-sm"
            data-testid={testId}
          >
            {/* Empty placeholder — the floating label serves this role */}
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>{children}</SelectContent>
        </Select>
      </div>

      {/* Label — positioned relative to outer div, not clipped by border wrapper */}
      <label
        htmlFor={id}
        className={cn(
          floatingLabelVariants({ variant, floating: isFloating }),
          'pointer-events-none',
        )}
      >
        {label}
      </label>
    </div>
  );
}
